import { useMemo } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { BsPerson, BsEar, BsScissors, BsEyeglasses } from 'react-icons/bs';
import { IoEyeOutline, IoShirtOutline, IoColorPaletteOutline } from 'react-icons/io5';
import { GiNoseSide, GiLips, GiTopHat } from 'react-icons/gi';
import { RiHeart2Line } from 'react-icons/ri';

import {
  hairStyleOptions,
  eyesOptions,
  noseOptions,
  mouthOptions,
  glassesOptions,
  hatStyleOptions,
  shirtOptions,
  earSizeOptions,
} from '../options/avatarOptions';

interface AvatarDraftProps {
  userAvatar: any;
  setUserAvatar: (avatar: any) => void;
  primaryColor: string;
}

type CategoryConfig = {
  id: string;
  name: string;
  icon: React.ReactNode;
  field: string;
  options?: { value: string; label: string }[];
  colorField?: string;
};

export default function AvatarDraft({ userAvatar, setUserAvatar, primaryColor }: AvatarDraftProps) {

  const categories: CategoryConfig[] = useMemo(() => [
    { id: 'rosto', name: 'Rosto', icon: <BsPerson size={18} />, field: 'faceColor', colorField: 'faceColor' },
    { id: 'orelhas', name: 'Orelhas', icon: <BsEar size={18} />, field: 'earSize', options: earSizeOptions },
    { id: 'cabelo', name: 'Cabelo', icon: <BsScissors size={18} />, field: 'hairStyle', options: hairStyleOptions, colorField: 'hairColor' },
    { id: 'olhos', name: 'Olhos', icon: <IoEyeOutline size={18} />, field: 'eyeStyle', options: eyesOptions },
    { id: 'nariz', name: 'Nariz', icon: <GiNoseSide size={18} />, field: 'noseStyle', options: noseOptions },
    { id: 'boca', name: 'Boca', icon: <GiLips size={18} />, field: 'mouthStyle', options: mouthOptions },
    { id: 'oculos', name: 'Óculos', icon: <BsEyeglasses size={18} />, field: 'glassesStyle', options: glassesOptions },
    { id: 'chapeu', name: 'Chapéu', icon: <GiTopHat size={18} />, field: 'hatStyle', options: hatStyleOptions, colorField: 'hatColor' },
    { id: 'roupa', name: 'Roupa', icon: <IoShirtOutline size={18} />, field: 'shirtStyle', options: shirtOptions, colorField: 'shirtColor' },
    { id: 'fundo', name: 'Fundo', icon: <IoColorPaletteOutline size={18} />, field: 'bgColor', colorField: 'bgColor' },
  ], []);

  const updateField = (field: string, value: string) => {
    setUserAvatar((prev: any) => ({ ...prev, [field]: value }));
  };

  const navigate = (cat: CategoryConfig, direction: 'prev' | 'next') => {
    if (!cat.options) return;
    const currentIndex = cat.options.findIndex(o => o.value === userAvatar?.[cat.field]);
    let nextIndex: number;
    if (direction === 'next') {
      nextIndex = currentIndex < cat.options.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : cat.options.length - 1;
    }
    updateField(cat.field, cat.options[nextIndex].value);
  };

  const getCurrentLabel = (cat: CategoryConfig) => {
    if (!cat.options) return '';
    const found = cat.options.find(o => o.value === userAvatar?.[cat.field]);
    return found?.label ?? cat.options[0]?.label ?? '';
  };

  return (
    <div className="flex flex-col gap-3">
      {categories.map((cat) => {
        const hasOptions = !!cat.options;
        const hasColor = !!cat.colorField;
        const isColorOnly = !cat.options && !!cat.colorField;

        return (
          <div
            key={cat.id}
            className="relative flex items-center bg-white rounded-2xl px-3 md:px-4 py-3 md:py-6 pr-12 md:pr-16 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-2 md:gap-3 min-w-[100px] md:min-w-[160px]">
              <div
                className="w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-white flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                {cat.icon}
              </div>

              <span className="text-xs md:text-base font-semibold text-gray-800 uppercase tracking-wide">
                {cat.name}
              </span>
            </div>

            {hasOptions && (
              <div className="absolute left-[40%] md:left-1/2 -translate-x-4 flex items-center gap-0.5 md:gap-1">
                <button
                  onClick={() => navigate(cat, 'prev')}
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
                >
                  <FiChevronLeft size={16} className="md:w-[18px] md:h-[18px]" />
                </button>
                <span className="text-xs md:text-base text-gray-700 font-medium min-w-[60px] md:min-w-[90px] text-center">
                  {getCurrentLabel(cat)}
                </span>
                <button
                  onClick={() => navigate(cat, 'next')}
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
                >
                  <FiChevronRight size={16} className="md:w-[18px] md:h-[18px]" />
                </button>
              </div>
            )}

            {isColorOnly && <div className="flex-1" />}

            {hasColor && (
              <div className="absolute right-3 md:right-4">
                <label className="relative w-8 h-8 md:w-9 md:h-9 rounded-full block cursor-pointer overflow-hidden border-2 border-gray-200 hover:border-gray-400 transition-colors shadow-sm">
                  <input
                    type="color"
                    value={userAvatar?.[cat.colorField ?? ''] || '#000000'}
                    onChange={(e) => updateField(cat.colorField ?? '', e.target.value)}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                  />
                  <span
                    className="block w-full h-full rounded-full"
                    style={{ backgroundColor: userAvatar?.[cat.colorField ?? ''] || '#000000' }}
                  />
                </label>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
